import { TOKEN_KEY } from '@/AppConfig';
import {
  checkAuth,
  clearLocalStorage,
  getLocalStorage,
  getLogoutPath,
} from '@/utils/common';
import axios, { AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import { addActingFor } from '@/views/approvals/Setup/actingFor';
import { reportApiFailure, reportApiOk } from '@/global/serviceStatus';

//setup default configurations for axios

axios.defaults.baseURL = import.meta.env.APP_API_BASE_URL;
axios.defaults.timeout = 30000; // 30 second timeout to prevent hanging requests
axios.defaults.headers.post['Content-Type'] = 'application/json';
axios.defaults.headers.put['Content-Type'] = 'application/json';
axios.defaults.headers.patch['Content-Type'] = 'application/json';

// Configure axios retry for resilience
axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return (
      axiosRetry.isNetworkOrIdempotentRequestError(error) ||
      (error.response?.status ?? 0) >= 500
    );
  },
  onRetry: (retryCount, _error, requestConfig) => {
    if (import.meta.env.DEV) {
      console.warn(`Retrying request (${retryCount}/3):`, requestConfig.url);
    }
  },
});

//interceptor for axio request handling

axios.interceptors.request.use(
  function (config) {
    if (getLocalStorage(TOKEN_KEY)) {
      config.headers['X-Access-Key'] = getLocalStorage(TOKEN_KEY);
    }
    // Approval Inbox acting for a delegator (Approvals > Delegation)
    config.data = addActingFor(config.url, config.data);
    // Debug logging in development
    if (import.meta.env.DEV) {
      console.log('Request:', config.method?.toUpperCase(), config.url);
      console.log('Payload:', config.data);
    }
    return config;
  },
  function (error: AxiosError) {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    if (error.response?.data) {
      return Promise.reject(error.response.data);
    } else {
      return Promise.reject(error.response);
    }
  }
);

axios.interceptors.response.use(
  (response) => {
    reportApiOk();
    return response;
  },
  function (error: AxiosError) {
    // no answer at all, or a server/proxy error (after axios-retry gave up): is the server down? The check pings the
    // API itself, so an ordinary error from a working API does not show the connection screen.
    // (a timeout is a slow request, not an outage)
    const status = error?.response?.status ?? 0;
    if (!axios.isCancel(error) && error?.code !== 'ECONNABORTED' && (!error?.response || status >= 500)) {
      reportApiFailure();
    } else if (error?.response) {
      reportApiOk();
    }
    if (error?.response?.status === 401) {
      if (checkAuth()) {
        clearLocalStorage();
        window.location.href = getLogoutPath();
      }
    }
    return Promise.reject(error);
  }
);
